/**
 * MiniCalendar — small month-grid date picker for the Schedule page's
 * calendar-jump sidebar. Mon-first grid (matches the week math already used
 * by SchedulePageV2/weekly-planner), today ringed, selected date filled, a
 * dot under any date present in `markedDates`.
 */
import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { T2 } from './AppShellV2.tsx';

const WEEKDAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dateKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Mon-first 6-row grid covering the full month plus lead/trail days from adjacent months. */
function buildGrid(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const leadDays = first.getDay() === 0 ? 6 : first.getDay() - 1; // days before the 1st to reach Monday
  const start = new Date(first);
  start.setDate(first.getDate() - leadDays);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

interface MiniCalendarProps {
  month: Date;
  onMonthChange: (d: Date) => void;
  selectedDate: Date;
  onSelectDate: (d: Date) => void;
  markedDates: Map<string, number>;
}

export default function MiniCalendar({ month, onMonthChange, selectedDate, onSelectDate, markedDates }: MiniCalendarProps) {
  const cells = useMemo(() => buildGrid(month), [month]);
  const today = new Date();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: T2.ink }}>
          {month.toLocaleDateString([], { month: 'long', year: 'numeric' })}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => { const n = new Date(month); n.setMonth(month.getMonth() - 1); onMonthChange(n); }}
            style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'none', color: T2.ink3, cursor: 'pointer', borderRadius: 6 }}
          >
            <ChevronLeft size={13} />
          </button>
          <button
            onClick={() => { const n = new Date(month); n.setMonth(month.getMonth() + 1); onMonthChange(n); }}
            style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'none', color: T2.ink3, cursor: 'pointer', borderRadius: 6 }}
          >
            <ChevronRight size={13} />
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {WEEKDAY_LABELS.map((w, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 9.5, fontWeight: 700, color: T2.ink3, padding: '2px 0' }}>{w}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === month.getMonth();
          const isToday = isSameDay(d, today);
          const isSelected = isSameDay(d, selectedDate);
          const count = markedDates.get(dateKey(d)) || 0;
          return (
            <button
              key={i}
              onClick={() => onSelectDate(d)}
              style={{
                position: 'relative', aspectRatio: '1', border: 'none', borderRadius: 8, cursor: 'pointer',
                fontSize: 11, fontWeight: isToday || isSelected ? 700 : 500,
                background: isSelected ? T2.accent : 'transparent',
                color: isSelected ? 'var(--lyra-color-surface)' : !inMonth ? T2.border : isToday ? T2.accentText : T2.ink2,
                boxShadow: isToday && !isSelected ? `inset 0 0 0 1.5px ${T2.accent}` : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {d.getDate()}
              {count > 0 && (
                <span style={{
                  position: 'absolute', bottom: 3, width: 3.5, height: 3.5, borderRadius: '50%',
                  background: isSelected ? 'var(--lyra-color-surface)' : T2.accent,
                }} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { isSameDay, dateKey };
